'use client'

import { useState } from 'react'
import { Trash2, Download, Upload, FileText, Search, Filter, Eye, Edit, ScrollText, Folder } from 'lucide-react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import type { SavedScene } from '@creanizer/types'
import { SceneEditModal } from './SceneEditModal'
import { useSceneStore } from '../../../../stores/sceneStore'
import { useFolderStore } from '../../../../stores/folderStore'
import { ExportImportButtons } from '../../../../components/common'
import { FolderManager, FolderMoveSelect } from './FolderManager'

interface SceneSavedProps {
  scenes: SavedScene[]
  onExport: () => void
  onImport: () => void
  onRemove: (id: string) => void
  onSelect: (scene: SavedScene) => void
}

export function SceneSaved({ 
  scenes, 
  onExport, 
  onImport, 
  onRemove,
  onSelect
}: SceneSavedProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterMode, setFilterMode] = useState<string>('all')
  const [sortBy, setSortBy] = useState<'date' | 'title' | 'length'>('date')
  const [editingScene, setEditingScene] = useState<SavedScene | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [showFolders, setShowFolders] = useState(true)
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
  
  const { updateScene, exportSingleAsText, exportSingleAsJSON } = useSceneStore()
  const { getItemsInFolder, getItemFolder, moveItemBetweenFolders } = useFolderStore()
  
  // フォルダによるフィルタリング
  let folderFilteredScenes = scenes
  if (selectedFolderId === 'all') {
    folderFilteredScenes = scenes
  } else if (selectedFolderId === null) {
    // 未整理: どのフォルダにも属していないアイテム
    const allFolderItemIds = new Set<string>()
    const folders = useFolderStore.getState().getFolders('scene')
    folders.forEach(folder => {
      getItemsInFolder('scene', folder.id).forEach(id => allFolderItemIds.add(id))
    })
    folderFilteredScenes = scenes.filter(scene => !allFolderItemIds.has(scene.id))
  } else {
    const itemIds = getItemsInFolder('scene', selectedFolderId)
    folderFilteredScenes = scenes.filter(scene => itemIds.includes(scene.id))
  }

  // フィルタリングと検索
  const filteredScenes = folderFilteredScenes.filter(scene => {
    // 検索クエリでフィルタ
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      const title = scene.result?.title || (scene as any).title || ''
      const fullText = scene.result?.fullText || ''
      const summary = scene.result?.summary || ''
      
      if (
        !title.toLowerCase().includes(query) &&
        !fullText.toLowerCase().includes(query) &&
        !summary.toLowerCase().includes(query)
      ) {
        return false
      }
    }

    // モードでフィルタ
    const inputMode = scene.input?.mode || 'normal'
    if (filterMode !== 'all' && inputMode !== filterMode) {
      return false
    }

    return true
  })

  // ソート
  const sortedScenes = [...filteredScenes].sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return b.timestamp - a.timestamp
      case 'title':
        const titleA = a.result?.title || (a as any).title || ''
        const titleB = b.result?.title || (b as any).title || ''
        return titleA.localeCompare(titleB)
      case 'length':
        const wordCountA = a.result?.wordCount || a.result?.fullText?.length || 0
        const wordCountB = b.result?.wordCount || b.result?.fullText?.length || 0
        return wordCountB - wordCountA
      default:
        return 0
    }
  })

  const getModeLabel = (mode?: string) => {
    switch (mode) {
      case 'standard': return '標準'
      case 'continuation': return '連続'
      case 'outline': return 'アウトライン'
      case 'rewrite': return 'リライト'
      default: return '標準'
    }
  }

  const getGenreLabel = (genre?: string) => {
    switch (genre) {
      case 'fantasy': return 'ファンタジー'
      case 'sf': return 'SF'
      case 'modern': return '現代'
      case 'mystery': return 'ミステリー'
      case 'romance': return 'ロマンス'
      case 'horror': return 'ホラー'
      case 'historical': return '歴史'
      case 'comedy': return 'コメディ'
      case 'literary': return '文学'
      default: return genre || '未設定'
    }
  }

  return (
    <div className="card max-w-full overflow-x-hidden">
      <div className="flex items-center gap-2 mb-2">
        <ScrollText className="h-5 w-5" />
        <h2 className="text-xl font-semibold">
          保存済みシーン ({scenes.length}/500)
        </h2>
      </div>
      <p className="text-muted-foreground mb-6">
        生成したシーン描写を保存・管理
      </p>
      
      <div className="space-y-4 max-w-full">
        {/* 検索・フィルター */}
        <div className="space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="シーンを検索..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFolders(!showFolders)}
              className="px-2"
            >
              <Folder className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="flex gap-2">
            <Select value={filterMode} onValueChange={setFilterMode}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全モード</SelectItem>
                <SelectItem value="standard">標準</SelectItem>
                <SelectItem value="continuation">連続</SelectItem>
                <SelectItem value="outline">アウトライン</SelectItem>
                <SelectItem value="rewrite">リライト</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={(value) => setSortBy(value as any)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">日付順</SelectItem>
                <SelectItem value="title">タイトル順</SelectItem>
                <SelectItem value="length">文字数順</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* エクスポート・インポートボタン */}
        <div className="flex gap-2">
          <ExportImportButtons
            onExportText={onExport}
            onExportJSON={onExport}
            onImportJSON={onImport}
            dataTypeName="シーン"
          />
          
          {scenes.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (confirm('すべてのシーンを削除しますか？')) {
                  scenes.forEach(scene => onRemove(scene.id))
                }
              }}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              全削除
            </Button>
          )}
        </div>

        <div className="flex gap-4">
          {/* フォルダサイドバー */}
          {showFolders && (
            <div className="w-48 border-r pr-4">
              <FolderManager
                generatorType="scene"
                items={scenes}
                selectedFolderId={selectedFolderId}
                onFolderSelect={setSelectedFolderId}
                draggedItemId={draggedItemId}
                onItemMove={(itemId, toFolderId) => {
                  const currentFolder = getItemFolder('scene', itemId)
                  moveItemBetweenFolders('scene', itemId, currentFolder?.id || null, toFolderId)
                }}
              />
            </div>
          )}
          
          {/* シーンリスト */}
          <div className="flex-1">
            <ScrollArea className="h-[500px] w-full overflow-x-hidden">
              <div className="space-y-2 max-w-full w-full">
                {sortedScenes.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                {searchQuery || filterMode !== 'all' 
                  ? '条件に一致するシーンがありません' 
                  : '保存されたシーンがありません'}
              </p>
                ) : (
                  sortedScenes.map((scene) => {
                    // resultがない古いデータへの対応
                    const title = scene.result?.title || (scene as any).title || `シーン ${scene.id.substring(0, 8)}`
                    const wordCount = scene.result?.wordCount || (scene.result?.fullText?.length) || 0
                    const summary = scene.result?.summary || ''
                    const inputMode = scene.input?.mode || 'normal'
                    const inputGenre = scene.input?.genre || ''
                    
                    return (
                      <div 
                        key={scene.id} 
                        className="card p-2 sm:p-4 overflow-hidden max-w-full w-[calc(100%-2rem)] sm:w-full mr-auto cursor-move"
                        draggable
                        onDragStart={(e) => {
                          setDraggedItemId(scene.id)
                          e.dataTransfer.effectAllowed = 'move'
                        }}
                        onDragEnd={() => {
                          setDraggedItemId(null)
                        }}
                        style={{
                          opacity: draggedItemId === scene.id ? 0.5 : 1
                        }}
                      >
                    {/* モバイル版: 縦並びレイアウト */}
                    <div className="flex flex-col gap-3 sm:hidden w-full">
                      <div className="w-full">
                        <h3 className="font-medium text-sm truncate">{title}</h3>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                          <span className="truncate">{getModeLabel(inputMode)}</span>
                          {inputGenre && (
                            <>
                              <span>•</span>
                              <span className="truncate">{getGenreLabel(inputGenre)}</span>
                            </>
                          )}
                          <span>•</span>
                          <span>{wordCount}文字</span>
                        </div>
                        {summary && (
                          <p className="text-xs text-muted-foreground mt-2 truncate">
                            {summary}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(scene.timestamp).toLocaleDateString('ja-JP', {
                            year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    
                    {/* モバイル版: 横一列のボタン */}
                    <div className="flex gap-0.5 justify-start w-full">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onSelect(scene)}
                        title="読込"
                        className="w-12 h-7 px-1"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingScene(scene)}
                        title="編集"
                        className="w-12 h-7 px-1"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleAsText(scene.id)}
                        title="テキスト形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">txt</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportSingleAsJSON(scene.id)}
                        title="JSON形式でエクスポート"
                        className="w-14 h-7 px-0.5 text-[9px]"
                      >
                        <Download className="h-2.5 w-2.5" />
                        <span className="ml-0.5">json</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('このシーンを削除しますか？')) {
                            onRemove(scene.id)
                          }
                        }}
                        className="w-12 h-7 px-1"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* PC版: 既存の横並びレイアウト */}
                  <div className="hidden sm:flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <h3 className="font-semibold text-sm truncate">{title}</h3>
                      
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                        <span className="truncate">{getModeLabel(inputMode)}</span>
                        {inputGenre && (
                          <>
                            <span>•</span>
                            <span className="truncate">{getGenreLabel(inputGenre)}</span>
                          </>
                        )}
                        <span>•</span>
                        <span>{wordCount}文字</span>
                      </div>
                      
                      {summary && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {summary}
                        </p>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(scene.timestamp).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      
                      {scene.result?.metadata?.continuationInfo && (
                        <p className="text-xs text-blue-600 mt-1">
                          連続生成 ({scene.result.metadata.continuationInfo.continuationType})
                        </p>
                      )}
                      
                      {/* フォルダ選択（PC版） */}
                      <div className="mt-2 w-32">
                        <FolderMoveSelect
                          generatorType="scene"
                          itemId={scene.id}
                          currentFolderId={getItemFolder('scene', scene.id)?.id}
                          onMove={() => {}}
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onSelect(scene)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          読込
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsText(scene.id)}
                          title="テキスト形式でエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          txt
                        </Button>
                      </div>
                      
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingScene(scene)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          編集
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportSingleAsJSON(scene.id)}
                          title="JSON形式でエクスポート"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          json
                        </Button>
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (confirm('このシーンを削除しますか？')) {
                            onRemove(scene.id)
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )
            })
            )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>

      {scenes.length >= 450 && (
        <p className="text-xs text-muted-foreground text-center">
          保存可能件数の上限（500件）に近づいています
        </p>
      )}
      
      {editingScene && (
        <SceneEditModal
          isOpen={!!editingScene}
          onClose={() => setEditingScene(null)}
          onSave={(updatedResult) => {
            updateScene(editingScene.id, updatedResult)
            setEditingScene(null)
          }}
          sceneResult={editingScene.result}
          title="シーンを編集"
        />
      )}
    </div>
  )
}